import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import _ from 'lodash';
import moment from 'moment';

import withChartExport from '../../../components/ChartExporting';
import dateUtils from '../../../util/dates';
import { getSimulationResults } from '../MonteCarloActions';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

let chartConfig = {
  credits: {
    enabled: false,
  },
  chart: {
    backgroundColor: null,
    type: 'column',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: 70,
    events: {
      load: (e) => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      },
    },
  },
  title: {
    text: '',
  },
  navigator: {
    enabled: true,
  },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    labels: {
      style: {
        fontSize: '12px'
      },
    },
    tickmarkPlacement: 'on',
    title: {
      enable: false,
    },
  },
  yAxis: {
    opposite: false,
    allowDecimals: false,
    labels: {
      style: {
        fontSize: '12px'
      },
    },
    min: 0,
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Frequency (Number of trials)',
      style: {
        fontSize: '13px'
      },
    },
  },
  legend: {
    enabled: false,
  },
  plotOptions: {
    series: {
      stickyTracking: false,
    },
    column: {
      stacking: 'normal',
      pointPadding: 0,
      borderWidth: 1,
      groupPadding: 0,
      shadow: false,
      pointRange: 24 * 3600 * 1000,
      states: {
        hover: {
          brightness: -0.05,
        },
      },
    },
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0,
  },
  series: [
    {
      name: 'Data',
      data: [],
    },
  ],
};

let masterchartConfig = {
  credits: {
    enabled: false,
  },
  exporting: {
    enabled: false,
  },
  chart: {
    type: 'column',
    reflow: false,
    borderWidth: 0,
    backgroundColor: null,
    marginLeft: 70,
    marginRight: 10,
    zoomType: 'xy',
    events: {},
  },
  title: {
    text: '',
  },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    showLastTickLabel: true,
    plotBands: [
      {
        id: 'mask-before',
        color: 'rgba(0, 0, 0, 0.1)',
      },
    ],
    title: {
      text: null,
    },
    labels: {
      style: {
        fontSize: '12px'
      },
    },
  },
  yAxis: {
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Daily Throughput',
      style: {
        fontSize: '13px'
      },
    },
    labels: {
      style: {
        fontSize: '12px'
      },
    },
  },
  plotOptions: {
    series: {
      marker: {
        enabled: false,
      },
      shadow: false,
      states: {
        hover: {
          lineWidth: 1,
        },
      },
      enableMouseTracking: false,
    },
    column: {
      stacking: 'normal',
    },
  },
  tooltip: {
    formatter: function () {
      return false;
    },
  },
  legend: {
    enabled: false,
  },
  series: [
    {
      name: 'Data',
      data: [],
    },
  ],
};

class MonteCarloChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      baseData: [],
    };

    this.onSelection = this.onSelection.bind(this);
  }

  componentDidMount = () => {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    const that = this;
    chartConfig.tooltip.formatter = function () {
      return that.tooltipFormatter(this, that);
    };

    chartConfig.exporting = this.props.chartExporting;
    masterchartConfig.chart.events.selection = this.onSelection;

    this.master = new Highcharts['Chart'](`${this.props.id}-master`, masterchartConfig);
    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);

    if (this.props.data && this.props.data.length) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  };

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.data, this.props.data)) {
      this.updateChart();
      this.props.getSimulationResults(this.state.baseData.length ? this.state.baseData : this.props.data);
    }

    if (!_.isEqual(prevProps.simData, this.props.simData)) {
      this.updateMonteCarloChart();
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (!!this.props.simData && prevProps.percentiles != this.props.percentiles) {
      this.chart.update({
        xAxis: {
          plotLines: this.renderPercentiles(this.props.simData),
        },
      });
    }

    if (prevProps.showNavigator != this.props.showNavigator) {
      this.master.reflow();
      this.chart.reflow();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Monte Carlo: Delivery Date',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },          
            }
          },
        },
        false
      );
    }

    if (
      _.isEqual(prevProps.data, this.props.data) &&
      (prevProps.trials !== this.props.trials ||
        prevProps.completedItems !== this.props.completedItems ||
        prevProps.date !== this.props.date ||
        prevProps.scale !== this.props.scale)
    ) {
      this.props.getSimulationResults(this.state.baseData.length ? this.state.baseData : this.props.data);
    }

    if (prevProps.scale !== this.props.scale) {
      this.scaleTRC();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
    this.master.destroy();
  }

  scaleTRC = () => {
    while (this.master.series.length > 0) {
      this.master.series[0].remove(false);
    }

    for (let i = 0; i < this.props.data.length; i++) {
      const data = { ...this.props.data[i] };
      data.data = _.map(data.data, (p) => {
        return {
          ...p,
          y: p.y * +this.props.scale,
        };
      });
      this.master.addSeries(data, false);
    }

    this.master.redraw();
  };

  updateMonteCarloChart() {
    if (this.props.simData.length) {
      this.chart.update({
        xAxis: {
          plotLines: this.renderPercentiles(this.props.simData),
        },
        series: [
          {
            data: this.props.simData,
          },
        ],
      });
    } else {
      this.chart.update({
        series: [
          {
            data: [],
          },
        ],
      });
    }
  }

  updateChart() {
    while (this.master.series.length > 0) {
      this.master.series[0].remove(false);
    }

    for (let i = 0; i < this.props.data.length; i++) {
      this.master.addSeries(this.props.data[i], false);
    }

    this.master.redraw();
    this.scaleTRC();
  }

  updateTimeLabelsFormat(dateFormat) {
    let config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.master.update(config);
    this.chart.update(config);
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('mc', this.props.wallmode);
    this.chart.update(newConfig.chart);
    this.master.update(newConfig.master);
  }

  calculatePercentiles = () => {
    if (this.props.simData.length) {
      const vals = _(this.props.simData)
        .orderBy('x')
        .reduce((arr, d) => {
          for (let i = 0; i < d.y; i++) {
            arr.push(d.x);
          }
          return arr;
        }, []);

      return _.map(this.props.allPercentileValues, (p) => {
        return {
          name: p,
          value: vals[Math.ceil((vals.length * p) / 100) - 1],
        };
      });
    }

    return [];
  };

  renderPercentiles = (data) => {
    const lines = [],
      ticks = [];
    let bars = 0;

    if (!!data.length) {
      const last = _.last(data.series);
      bars = last ? last.x + 1 : 0;
    }

    const percentiles = this.calculatePercentiles();

    for (let i = 0; i < percentiles.length; i++) {
      const pObj = _.find(percentiles, (item) => item.name == this.props.percentiles[i]);
      if (!pObj) {
        continue;
      }

      const text = `${pObj.name}%`;
      this.addLine(lines, text, pObj.value);
      this.addTick(ticks, pObj.value);
    }

    const newLines = [];
    let lastValue = 0;
    let coll = _(lines)
      .groupBy('value')
      .toPairs()
      .orderBy((l) => {
        return +l[0];
      })
      .value();
    _.forEachRight(coll, (l) => {
      const line = l[1][0];
      const labels = _.map(l[1], (l) => {
        return l.label.text;
      });
      line.label.text = labels.join(', ');

      const diff = lastValue - +l[0];
      if (diff > 0 && diff < bars * 0.035) {
        line.label.x = -13;
      } else {
        line.label.x = 4;
      }

      newLines.push(line);
      lastValue = +l[0];
    });

    const newTicks = [];
    lastValue = 0;
    coll = _(ticks)
      .groupBy('value')
      .toPairs()
      .orderBy((l) => {
        return +l[0];
      })
      .value();
    _.forEachRight(coll, (t) => {
      const tick = t[1][0];

      const diff = lastValue - +t[0];
      if (diff > 0 && diff < bars * 0.035) {
        tick.label.x = -20;
      } else {
        tick.label.x = -6;
      }

      newTicks.push(tick);
      lastValue = +t[0];
    });

    return newLines.concat(newTicks);
  };

  addLine(lines, text, val) {
    lines.push({
      color: '#444444',
      dashStyle: 'dot',
      zIndex: 2,
      width: 2,
      zIndex: 10,
      value: +val,
      label: {
        verticalAlign: 'top',
        rotation: 90,
        text: text,
        style: {
          ...highchartUtils.getFontSizeConfig('percentile', this.props.wallmode)
        },
      },
    });
  }

  addTick(ticks, val) {
    ticks.push({
      color: '#444444',
      dashStyle: 'dot',
      width: 2,
      zIndex: 2,
      value: +val,
      label: {
        verticalAlign: 'bottom',
        textAlign: 'center',
        rotation: 0,
        x: -6,
        y: 22,
        // text: val,
        useHTML: true,
        style: {
          ...highchartUtils.getFontSizeConfig('percentile', this.props.wallmode),
          padding: '0 6px',
          backgroundColor: '#fcfcfc',
          color: '#444444',
          fontWeight: 'bold'
        },
      },
    });
  }

  onSelection(e) {
    var extremesObject = e.xAxis[0],
      min = extremesObject.min,
      max = extremesObject.max,
      xAxis = e.target.xAxis[0];

    xAxis.removePlotBand('mask-before');
    xAxis.addPlotBand({
      id: 'mask-before',
      from: xAxis.dataMin,
      to: min,
      color: 'rgba(0, 0, 0, 0.1)',
      borderColor: 'black',
      borderWidth: 1,
    });

    xAxis.removePlotBand('mask-after');
    xAxis.addPlotBand({
      id: 'mask-after',
      from: max,
      to: xAxis.dataMax,
      color: 'rgba(0, 0, 0, 0.1)',
      borderColor: 'black',
      borderWidth: 1,
    });

    if (this.master.resetBtn) {
      this.master.resetBtn.destroy();
    }
    this.master.resetBtn = this.master.renderer
      .button(
        'Reset zoom',
        null,
        null,
        () => {
          this.resetExtremes();
        },
        {
          zIndex: 20,
        }
      )
      .attr({
        align: 'right',
        title: 'Reset zoom level 1:1',
      })
      .add(this.master.zoomGroupButton)
      .align(
        {
          align: 'right',
          x: 5,
          y: 10,
        },
        false,
        null
      );

    const baseData = _.map(xAxis.series, (s) => {
      return {
        data: _(s.points)
          .map((p) => {
            if (p.x >= min && p.x <= max) {
              return { x: p.x, y: p.y };
            }
          })
          .filter((p) => !!p)
          .value(),
      };
    });

    this.setState(
      {
        baseData,
      },
      () => {
        this.props.getSimulationResults(this.state.baseData);
      }
    );

    return false;
  }

  resetExtremes() {
    this.master.xAxis[0].removePlotBand('mask-before');
    this.master.xAxis[0].removePlotBand('mask-after');
    this.master.resetBtn.destroy();
    delete this.master.resetBtn;

    this.setState({
      baseData: [],
    });
    this.props.getSimulationResults(this.props.data);
  }

  tooltipFormatter(chartScope, componentScope) {
    let probability = +chartScope.point.probability;
    if (probability <= 1) {
      probability = '<1';
    } else if (probability >= 99) {
      probability = '>99';
    }

    const tooltip = `<p style="font-size: 12px; margin: 5px 3px; white-space: nowrap;">Date: ${moment(chartScope.x).format(
      componentScope.props.dateFormat
    )}</p>
    <p style="font-size: 12px; margin: 5px 3px 0; white-space: nowrap;">Frequency: ${chartScope.y}</p>
    <p style="font-size: 12px; margin: 5px 3px 0; white-space: nowrap;">Probability: ${probability}%</p>`;
    return tooltip;
  }

  render() {
    return (
      <div className="chart_content chart_tooltip_style">
        <div
          id={`${this.props.id}-details`}
          className={'chart_details ' + (!this.props.showNavigator ? 'chart_details-full ' : ' ')}
        ></div>
        <div
          style={{ display: this.props.showNavigator ? 'block' : 'none' }}
          id={`${this.props.id}-master`}
          className="chart_master"
        ></div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getSimulationResults: (baseData) => dispatch(getSimulationResults(baseData)),
  };
};

const mapStateToProps = (state) => {
  return {
    simData: state.mc.simData,
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    showNavigator: state.filters.mc.showNavigator,
    allPercentileValues: state.filters.allPercentileValues,
    percentiles: state.filters.mc.selectedPercentiles,
    trials: state.filters.mc.trials,
    date: state.filters.mc.date,
    completedItems: state.filters.mc.completedItems,
    scale: state.filters.mc.scale,
    wallmode: state.app.wallmode,
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(MonteCarloChart));
